import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CONFIGURAZIONE_PREDEFINITA, daQuery, type ConfigurazioneSimulata } from '@emacomfort/device-client';
import { hostDemo } from './marca';

/**
 * Lo stato "sto guardando il simulatore, con questa configurazione".
 *
 * Perché non basta l'URL. La configurazione *nasce* nell'URL di
 * `/simulatore` — è quello che la rende un link da mandare a qualcuno — ma i
 * collegamenti interni dell'app (`<Link to="/zone">`) non portano con sé la
 * query, quindi al primo spostamento la scelta sarebbe persa e l'app
 * tornerebbe ai dati reali a metà navigazione. La scelta va quindi ricordata
 * finché non viene revocata.
 *
 * Perché `sessionStorage` e non `localStorage`. Il simulatore è una modalità
 * di prova, non una preferenza: deve finire quando finisce la sessione. Un
 * `localStorage` significherebbe che una prova fatta una volta continua a
 * nascondere l'impianto vero ogni volta che si riapre l'app — esattamente il
 * modo in cui una demo si traveste da guasto. Le preferenze vere
 * (`preferenze.ts`) restano su `localStorage`, che è la scelta giusta per
 * loro.
 */

interface StatoSimulatore {
  /** true quando l'app deve usare il simulatore invece dei dati reali. */
  attivo: boolean;
  configurazione: ConfigurazioneSimulata;
  avvia(configurazione: ConfigurazioneSimulata): void;
  /** Torna ai dati reali (dove esistono). */
  esci(): void;
}

const CHIAVE = 'emacomfort.simulatore';

export const useSimulatore = create<StatoSimulatore>()(
  persist(
    (set) => ({
      attivo: false,
      configurazione: CONFIGURAZIONE_PREDEFINITA,
      avvia: (configurazione) => set({ attivo: true, configurazione }),
      esci: () => set({ attivo: false }),
    }),
    { name: CHIAVE, storage: archivioDiSessione() },
  ),
);

/**
 * Letto UNA volta all'avvio da `richiedeLoginQui()`, prima che React monti —
 * quindi legge `sessionStorage` direttamente invece dello store, che a quel
 * punto potrebbe non essersi ancora reidratato. Non lancia mai: uno storage
 * negato (finestra privata, cookie bloccati) significa semplicemente "nessun
 * simulatore in corso", che è il default corretto.
 */
export function simulatoreAttivo(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const grezzo = window.sessionStorage.getItem(CHIAVE);
    if (!grezzo) return false;
    return Boolean((JSON.parse(grezzo) as { state?: { attivo?: boolean } }).state?.attivo);
  } catch {
    return false;
  }
}

/** `sessionStorage` con ogni accesso protetto: uno storage negato non deve
 *  impedire l'avvio dell'app, solo far dimenticare la modalità. */
function archivioDiSessione() {
  return {
    getItem: (nome: string) => {
      try {
        const grezzo = window.sessionStorage.getItem(nome);
        return grezzo ? (JSON.parse(grezzo) as never) : null;
      } catch {
        return null;
      }
    },
    setItem: (nome: string, valore: unknown) => {
      try {
        window.sessionStorage.setItem(nome, JSON.stringify(valore));
      } catch {
        /* storage negato: la modalità vale solo per questa pagina */
      }
    },
    removeItem: (nome: string) => {
      try {
        window.sessionStorage.removeItem(nome);
      } catch {
        /* idem */
      }
    },
  };
}

// ── la configurazione che arriva dall'URL ─────────────────────────────────

/**
 * Le chiavi che `aQuery` scrive. Servono per una domanda sola: questo URL
 * PORTA una configurazione, oppure e' un indirizzo qualunque? Un `/pannello`
 * nudo non deve toccare la modalita' — ne' accenderla, ne' spegnerla.
 */
const CHIAVI = ['centralina', 'zone', 'acs', 'vmc', 'fv', 'generatori', 'esterna', 'umidita', 'centrale', 'puffer'];

/**
 * La configurazione portata dall'URL, se ce n'e' una da applicare QUI.
 *
 * Prima esisteva un solo ingresso: `/simulatore`, la pagina che compone
 * l'impianto e poi apre l'app su di esso. Da quando il banco di prova sta sul
 * sito (`www.emacomfort.com`) l'ingresso e' un LINK, e
 * un link punta all'area che si vuole vedere — il pannello del residente,
 * l'area tecnica, il parco impianti — non al configuratore. Quindi la
 * configurazione va letta su qualsiasi rotta, non solo su una.
 *
 * Tre condizioni, tutte necessarie:
 *
 * 1. **Solo su un host demo** (`hostDemo`: vetrina o macchina di sviluppo).
 *    Su `cstai.emacomfort.com` l'app e' l'impianto di qualcuno: un link con
 *    `?zone=6` che glielo sostituisce con uno inventato e' esattamente il modo
 *    in cui una demo si traveste da guasto. La', questi parametri sono inerti.
 * 2. **Solo se l'URL porta davvero una configurazione.** Nessuna delle chiavi
 *    presenti significa "nessuna richiesta", non "configurazione predefinita":
 *    altrimenti ogni navigazione nuda azzererebbe l'impianto in corso.
 * 3. **Non su `/simulatore`.** Li' la pagina possiede la query — la riscrive a
 *    ogni interruttore — e la modalita' si accende quando si preme «Apri».
 *    Accenderla qui significherebbe entrare in simulazione al solo atterrare
 *    sul configuratore, con il pulsante d'uscita gia' visibile.
 */
export function configurazioneDaUrl({
  hostname,
  pathname,
  search,
}: {
  hostname: string;
  pathname: string;
  search: string;
}): ConfigurazioneSimulata | null {
  if (!hostDemo(hostname)) return null;
  if (pathname === '/simulatore' || pathname.startsWith('/simulatore/')) return null;
  const params = new URLSearchParams(search);
  if (!CHIAVI.some((k) => params.has(k))) return null;
  return daQuery(params);
}

/**
 * Applica la configurazione dell'URL, se c'e'. Chiamata da `main.tsx` PRIMA
 * del primo render: il cancello di accesso e `DeviceProvider` leggono la
 * modalita' mentre montano, e accenderla dopo farebbe partire l'app sul parco
 * del marchio per poi sostituirlo — un lampeggio, su un impianto che non e'
 * quello del link.
 */
export function avviaDaUrl(posizione: { hostname: string; pathname: string; search: string }): boolean {
  const configurazione = configurazioneDaUrl(posizione);
  if (!configurazione) return false;
  useSimulatore.getState().avvia(configurazione);
  return true;
}
